/* A live replica of the Mediability Commercial OS prototype (AppShell · Sidebar · OpportunityPage), running the «Studio Bergen» case through the flow. Static data, real components. Three layouts: full (sidebar), compact (no sidebar), narrow (one column). */
import { useEffect, useRef, useState } from "react";
import { useCountUp } from "./motion";
import { ArrowLeft, BarChart3, Building2, CalendarClock, Check, ChevronDown, FileSearch, FileText, Kanban, LayoutGrid, MoreHorizontal, Package, PackageCheck, Plus, Search, Settings, Sparkles, Trophy } from "lucide-react";
import { Calculation, Overview, Quote, Requirements, Solution } from "./MbTabs";

const B = "/tilbod/mediability/";
const STAGES = [["new", "New"], ["discovery", "Discovery"], ["solution_design", "Solution design"], ["calculation", "Calculation"], ["quote", "Quote"], ["negotiation", "Negotiation"]];
const TABS = [["overview", "Overview"], ["requirements", "Requirements", 19], ["solution", "Solution", 2], ["calculation", "Calculation"], ["quote", "Quote", 2], ["files", "Files", 3], ["activity", "Activity"]];

/* the script: one beat per step */
export const SCRIPT = [
  { tab: "overview", stage: "discovery", ms: 4200 },
  { tab: "requirements", stage: "discovery", ms: 4200, toast: ["3 new requirements identified from e-mail", "NRK · Anne Hansen · structured by AI"] },
  { tab: "solution", stage: "solution_design", ms: 4600, toast: ["Solution ‘Recommended’ ready – GM 31.5 %", "19 requirements matched against the catalogue"] },
  { tab: "calculation", stage: "calculation", ms: 4200 },
  { tab: "quote", stage: "quote", ms: 4200, toast: ["Quote v2 opened by Anne Hansen", "2 minutes ago · 2 views"] },
  { tab: "overview", stage: "won", won: true, ms: 5200, toast: ["Order 10492 created in Visma Business", "Products, services, customer and contacts sent – nothing retyped"] },
];

const NAV = [
  [null, [["Overview", LayoutGrid], ["AI Workspace", Sparkles]]],
  ["Sales", [["Opportunities", Kanban, true], ["Quotes", FileText], ["Tenders", FileSearch]]],
  ["Workspace", [["Customers", Building2], ["Products", Package], ["Insights", BarChart3]]],
];

const Avatar = ({ className = "" }) => <img src={`${B}avatars/david.jpg`} alt="" className={`h-9 w-9 shrink-0 rounded-full object-cover ring-1 ring-black/[0.08] ${className}`} style={{ width: 36, height: 36 }} draggable={false} />;

const Side = () => (
  <aside className="flex w-[232px] shrink-0 flex-col border-r border-black/[0.06] bg-[hsl(var(--sidebar))]">
    <div className="px-5 pb-4 pt-5"><img src={`${B}brand/mediability-logo-dark-red.svg`} alt="Mediability" className="h-[24px] w-auto" draggable={false} /></div>
    <nav className="flex-1 space-y-5 px-3">
      {NAV.map(([label, items], gi) => (
        <div key={label || gi}>
          {label && <div className="mb-1.5 px-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</div>}
          <div className="space-y-0.5">
            {items.map(([t, Icon, active]) => (
              <div key={t} className={`flex h-[34px] items-center gap-2.5 rounded-lg px-2.5 text-[13.5px] font-medium ${active ? "bg-white text-foreground shadow-[0_1px_2px_hsl(30_12%_18%/0.06),0_0_0_1px_hsl(30_12%_18%/0.05)]" : "text-foreground/70"}`}>
                <Icon className={`h-4 w-4 ${active ? "text-primary" : "text-muted-foreground"}`} strokeWidth={active ? 2.2 : 1.9} />
                {t}
              </div>
            ))}
          </div>
        </div>
      ))}
    </nav>
    <div className="space-y-3 px-3 pb-3 pt-2">
      <div className="relative overflow-hidden rounded-xl bg-[#111214] p-3.5 text-white shadow-[0_1px_2px_hsl(0_0%_0%/0.2),0_12px_28px_-14px_hsl(0_0%_0%/0.6)]">
        <span className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full bg-[hsl(10_76%_61%/0.35)] blur-2xl" />
        <div className="relative flex items-center gap-2 text-[13px] font-semibold"><Sparkles className="h-4 w-4 text-[hsl(10_80%_70%)]" /> AI Workspace</div>
        <div className="relative mt-1.5 text-[12px] leading-snug text-white/60">From inquiry to signed proposal – designed, priced and written with you.</div>
      </div>
      <div className="flex h-[34px] items-center gap-2.5 rounded-lg px-2.5 text-[13.5px] font-medium text-foreground/70"><Settings className="h-4 w-4 text-muted-foreground" strokeWidth={1.9} /> Settings</div>
      <div className="flex items-center gap-2.5 rounded-xl border border-black/[0.06] bg-white px-2.5 py-2">
        <Avatar />
        <div className="min-w-0 flex-1 leading-tight"><div className="truncate text-[13px] font-semibold">David Besseberg</div><div className="truncate text-[11px] text-muted-foreground">Key Account Manager</div></div>
      </div>
    </div>
  </aside>
);

const Top = ({ mode }) => {
  const nar = mode === "narrow";
  return (
    <header className={`flex h-16 shrink-0 items-center justify-between gap-3 ${nar ? "px-4" : "px-6"}`}>
      {mode !== "full" && <img src={`${B}brand/mediability-logo-dark-red.svg`} alt="Mediability" className="h-[22px] w-auto shrink-0" draggable={false} />}
      <div className={`flex h-10 items-center gap-2.5 rounded-full border border-black/[0.07] bg-white px-4 text-[13px] text-muted-foreground shadow-[0_1px_2px_hsl(30_12%_18%/0.04)] ${mode === "full" ? "w-[420px]" : "min-w-0 flex-1"}`}>
        <Search className="h-4 w-4 shrink-0 text-muted-foreground/80" />
        <span className="flex-1 truncate">{nar ? "Search…" : "Search customers, opportunities, quotes…"}</span>
        {!nar && <span className="inline-flex h-5 items-center rounded-[5px] border border-black/[0.08] bg-white px-1.5 text-[10px] font-medium shadow-[0_1px_0_hsl(30_12%_18%/0.08)]">⌘K</span>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {!nar && <span className="inline-flex h-9 items-center gap-2 rounded-full border border-black/[0.09] bg-white px-4 text-[13px] font-medium shadow-[0_1px_2px_hsl(30_12%_18%/0.04)]"><Sparkles className="h-4 w-4 text-primary" /> Ask AI</span>}
        <span className="inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-full bg-primary px-4 text-[13px] font-medium text-white shadow-[0_1px_2px_hsl(10_60%_30%/0.18),0_8px_18px_-10px_hsl(10_76%_50%/0.6)]"><Plus className="h-4 w-4" /> {nar ? "New" : "New in Workspace"}</span>
        <Avatar className="ml-1.5" />
      </div>
    </header>
  );
};

const Pill = ({ children, className = "" }) => <span className={`inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/[0.07] bg-[hsl(40_10%_96%)] px-3 text-[12.5px] text-muted-foreground ${className}`}>{children}</span>;

const Head = ({ stage, won, nar }) => {
  const idx = won ? STAGES.length : STAGES.findIndex(([k]) => k === stage);
  const label = won ? "Won" : STAGES[idx][1];
  return (
    <div className={nar ? "px-4 pt-4" : "px-8 pt-5"}>
      <div className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground"><ArrowLeft className="h-3.5 w-3.5" /> Opportunities <span className="text-border">/</span> NRK</div>
      <div className={`mt-4 flex ${nar ? "flex-col gap-4" : "items-start justify-between gap-6"}`}>
        <div className="flex min-w-0 items-start gap-4">
          <span className="mt-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-black/[0.07] bg-[#0b2a7a] text-[13px] font-bold tracking-wide text-white">NRK</span>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">NRK</div>
            <h1 className={`mt-1 font-semibold leading-[1.08] tracking-[-0.03em] ${nar ? "text-[28px]" : "text-[34px]"}`}>Studio Bergen</h1>
            <div className="mt-3.5 flex flex-wrap items-center gap-2">
              <span className="inline-flex h-8 items-center gap-2 rounded-full border border-black/[0.09] bg-white px-3 text-[12.5px] font-medium shadow-[0_1px_2px_hsl(30_12%_18%/0.04)]"><span className="h-1.5 w-1.5 rounded-full transition-colors duration-500" style={{ backgroundColor: `hsl(var(--stage-${won ? "won" : stage}))` }} />{label} <ChevronDown className="h-3 w-3 text-muted-foreground" /></span>
              <Pill><span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#111] text-[9px] font-semibold text-white">DB</span> David Besseberg</Pill>
              <Pill><CalendarClock className="h-3.5 w-3.5" /> Decision <span className="font-medium tabular text-foreground">15 Nov</span></Pill>
              <Pill className={won ? "text-[hsl(var(--status-ok))]" : "text-[hsl(var(--status-warn))]"}><span className={`h-1.5 w-1.5 rounded-full ${won ? "bg-[hsl(var(--status-ok))]" : "bg-[hsl(var(--status-warn))] mb-pulse"}`} /> <span className="text-xs font-medium">{won ? "Won" : "Needs action"}</span></Pill>
            </div>
          </div>
        </div>
        <div className={`flex shrink-0 gap-3 ${nar ? "items-center justify-between" : "flex-col items-end"}`}>
          <div className={`flex flex-col ${nar ? "" : "items-end"}`}><span className={`font-semibold leading-none tabular tracking-[-0.03em] ${nar ? "text-[24px]" : "text-[30px]"}`} style={{ fontFamily: "var(--font-display)" }} data-num="">2.3 MNOK</span><span className="mt-1 text-[12.5px] text-muted-foreground">Margin <span className="font-medium tabular text-foreground" data-num="">31.5 %</span></span></div>
          <div className="flex items-center gap-2">
            {!nar && <span className="inline-flex h-9 items-center gap-2 rounded-full border border-black/[0.09] bg-white px-4 text-[13px] font-medium"><Sparkles className="h-4 w-4 text-primary" /> AI Workspace</span>}
            <span className="inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 text-[13px] font-medium text-white shadow-[0_1px_2px_hsl(10_60%_30%/0.18),0_8px_18px_-10px_hsl(10_76%_50%/0.6)]">{won ? <><PackageCheck className="h-4 w-4" /> Open handover</> : idx < 3 ? <><Sparkles className="h-4 w-4" /> {nar ? "Generate proposal" : "Generate solution proposal"}</> : idx < 5 ? <><FileText className="h-4 w-4" /> Generate quote</> : <><Trophy className="h-4 w-4" /> Mark as Won</>}</span>
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-black/[0.09] bg-white"><MoreHorizontal className="h-4 w-4" /></span>
          </div>
        </div>
      </div>
      {won ? (
        <div className="mt-5 flex items-center gap-2 rounded-xl border border-[hsl(var(--status-ok)/0.3)] bg-[hsl(var(--status-ok)/0.06)] px-4 py-2.5 text-sm mb-fade"><Check className="h-4 w-4 shrink-0 text-[hsl(var(--status-ok))]" /> <span className="min-w-0 truncate">Won on 21 Oct 2026. Order <span className="font-medium tabular">10492</span> sent to Visma Business.</span> {!nar && <span className="ml-1 font-medium underline underline-offset-2">Open handover</span>}</div>
      ) : (
        <ol className="mt-6 grid grid-cols-6 gap-1.5">
          {STAGES.map(([k, l], i) => (
            <li key={k} className="min-w-0">
              <span className={`block h-[5px] w-full rounded-full transition-colors duration-500 ${i === idx ? "bg-primary" : i < idx ? "bg-foreground/80" : "bg-black/[0.08]"}`} />
              <span className={`mt-1.5 block truncate text-[11px] font-medium ${i === idx ? "text-primary" : i < idx ? "text-foreground/80" : "text-muted-foreground/70"}`}>{l}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

const Tabs = ({ tab, onTab, nar }) => (
  <div className={`sticky top-0 z-10 mt-5 border-b border-black/[0.06] bg-background ${nar ? "px-4" : "px-8"}`}>
    <div className="mb-noscroll flex gap-2 overflow-x-auto" role="tablist">
      {TABS.map(([k, l, n]) => (
        <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => onTab(k)} data-testid={`mb-tab-${k}`} className={`relative -mb-px flex items-center gap-1.5 whitespace-nowrap px-3.5 py-3 text-[13.5px] font-medium ${tab === k ? "text-foreground after:absolute after:inset-x-3.5 after:-bottom-px after:h-[2px] after:rounded-full after:bg-primary" : "text-muted-foreground"}`}>
          {l}
          {n ? <span className={`rounded-full px-1.5 text-[11px] tabular ${tab === k ? "bg-primary/10 text-primary" : "bg-black/[0.06] text-muted-foreground"}`}>{n}</span> : null}
        </button>
      ))}
    </div>
  </div>
);

export const Toast = ({ t, nar }) => (
  <div key={t[0]} className={`mb-toast absolute z-20 flex items-start gap-3 rounded-xl border border-black/[0.08] bg-white p-3.5 shadow-[0_1px_2px_hsl(30_12%_18%/0.06),0_24px_60px_-24px_hsl(30_12%_18%/0.4)] ${nar ? "inset-x-4 bottom-5" : "bottom-6 right-6 w-[360px]"}`} data-testid="mb-toast">
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--status-ok)/0.12)] text-[hsl(var(--status-ok))]"><Check className="h-3 w-3" /></span>
    <div className="min-w-0 text-[13px] leading-5"><div className="font-medium">{t[0]}</div><div className="text-muted-foreground">{t[1]}</div></div>
  </div>
);

const BODY = { overview: Overview, requirements: Requirements, solution: Solution, calculation: Calculation, quote: Quote };

/* the opportunity page on its own (header, tabs, the open tab) – used inside the system mockup too */
export const MbOpportunity = ({ beat, tab, onTab = () => {}, mode = "full" }) => {
  const Body = BODY[tab] || Overview;
  const nar = mode === "narrow";
  return (
    <div className="h-full min-h-0 overflow-hidden" data-testid="mb-opportunity" data-tab={tab}>
      <Head stage={beat.stage} won={beat.won} nar={nar} />
      <Tabs tab={tab} onTab={onTab} nar={nar} />
      <div key={tab + (beat.won ? "w" : "")} className={`mb-fade ${nar ? "px-4 py-5" : "px-8 py-6"}`}><Body won={beat.won} narrow={nar} /></div>
    </div>
  );
};

export const MbApp = ({ beat, tab, onTab, mode = "full" }) => {
  const nar = mode === "narrow";
  return (
    <div className="mb flex h-full w-full overflow-hidden bg-background text-foreground" data-testid="mb-app" data-mode={mode}>
      {mode === "full" && <Side />}
      <div className="relative flex min-w-0 flex-1 flex-col">
        <Top mode={mode} />
        <div className="min-h-0 flex-1 overflow-hidden">
          <MbOpportunity beat={beat} tab={tab} onTab={onTab} mode={mode} />
        </div>
        {beat.toast && <Toast t={beat.toast} nar={nar} />}
      </div>
    </div>
  );
};

/* runs the script; a click on a tab takes over for a while */
export const useScript = (active) => {
  const [i, setI] = useState(0);
  const [manual, setManual] = useState(null);
  useEffect(() => {
    if (!active || manual) return undefined;
    const t = setTimeout(() => setI((x) => (x + 1) % SCRIPT.length), SCRIPT[i].ms);
    return () => clearTimeout(t);
  }, [i, active, manual]);
  useEffect(() => {
    if (!manual) return undefined;
    const t = setTimeout(() => setManual(null), 9000);
    return () => clearTimeout(t);
  }, [manual]);
  const beat = SCRIPT[i];
  return { beat: manual ? { ...beat, toast: null, won: false, stage: beat.won ? "quote" : beat.stage } : beat, tab: manual || beat.tab, onTab: setManual, i };
};

/* browser frame that lays the app out for its own width: full ≥ 1100, compact ≥ 700 (no sidebar), narrow below (one column,
   laid out for a phone – at least 390 px, so on a phone it renders about 1:1) – then scales to fit */
const geoFor = (w) => {
  const mode = w < 700 ? "narrow" : w < 1100 ? "compact" : "full";
  const vw = mode === "narrow" ? Math.max(390, w) : mode === "compact" ? Math.max(1040, w) : 1360;
  return { mode, vw, s: w / vw };
};

export const MbStage = ({ caption }) => {
  const ref = useRef(null);
  const [geo, setGeo] = useState({ mode: "full", vw: 1360, s: 0.86 });
  const [active, setActive] = useState(true);
  useEffect(() => {
    const el = ref.current;
    const ro = new ResizeObserver(([e]) => setGeo(geoFor(e.contentRect.width)));
    ro.observe(el);
    const io = new IntersectionObserver((entries) => setActive(entries[entries.length - 1].isIntersecting), { threshold: 0.05 });
    io.observe(el);
    return () => {
      ro.disconnect();
      io.disconnect();
    };
  }, []);
  const { beat, tab, onTab } = useScript(active);
  const ih = geo.mode === "narrow" ? 760 : 820;
  return (
    <div className="mb-frame" data-testid="tilbod-hero-mockup" data-beat={SCRIPT.indexOf(beat)} data-mode={geo.mode}>
      <div className="mb-chrome" aria-hidden="true">
        <span className="mb-dots"><i /><i /><i /></span>
        <span className="mb-url">os.mediability.no/opportunities/studio-bergen</span>
        <span className="mb-cap">{caption}</span>
      </div>
      <div ref={ref} className="mb-view" style={{ height: ih * geo.s }}>
        <div className="mb-scale" style={{ transform: `scale(${geo.s})`, width: geo.vw, height: ih }}>
          <MbApp beat={beat} tab={tab} onTab={onTab} mode={geo.mode} />
        </div>
      </div>
    </div>
  );
};

/* a still of the opportunity page – no script, no toast – laid out for its own width and scaled to fit (the hero) */
export const MbStill = ({ tab = "overview", stage = "discovery", url = "os.mediability.no/opportunities/studio-bergen", caption, testId = "tilbod-hero-still" }) => {
  const ref = useRef(null);
  const [geo, setGeo] = useState({ mode: "full", vw: 1360, s: 0.86 });
  const [active, setActive] = useState(false);
  useEffect(() => {
    const el = ref.current;
    const ro = new ResizeObserver(([e]) => setGeo(geoFor(e.contentRect.width)));
    ro.observe(el);
    /* the entry motion (and the numbers counting up) start when the still is in view */
    const io = new IntersectionObserver((entries) => setActive(entries[entries.length - 1].isIntersecting), { threshold: 0.15 });
    io.observe(el);
    return () => {
      ro.disconnect();
      io.disconnect();
    };
  }, []);
  useCountUp(ref, active, { delay: 700 });
  const ih = geo.mode === "narrow" ? 600 : 820;
  const beat = { tab, stage, ms: 0 };
  return (
    <div className="mb-frame" data-testid={testId} data-mode={geo.mode} data-active={active ? "1" : "0"} aria-hidden="true">
      <div className="mb-chrome">
        <span className="mb-dots"><i /><i /><i /></span>
        <span className="mb-url">{url}</span>
        <span className="mb-cap">{caption}</span>
      </div>
      <div ref={ref} className="mb-view pointer-events-none select-none" style={{ height: ih * geo.s }}>
        <div className="mb-scale mb-cine" style={{ transform: `scale(${geo.s})`, width: geo.vw, height: ih }}>
          <MbApp beat={beat} tab={tab} onTab={() => {}} mode={geo.mode} />
        </div>
      </div>
    </div>
  );
};
